// src/shared/changelog/useChangelogNewItems.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAxiosAuth0 } from "@axios-config/useAxiosAuth0";
import { canSeeChangelog } from "@shared/config/featureFlags";
import {
  accountAreaItems,
  eventManagementItems,
  insightMenuItems,
  mainItems,
  managementItems,
  reflectorV2MenuItems,
  serviceItems,
  type MenuItem,
} from "@shared/navigation/SideMenu/MenuData";
import { findMenuItemByPath } from "@shared/navigation/SideMenu/MenuUtils";
import { getLatestReleaseGroup } from "./getChangelog";
import { ChangelogEntry } from "./types";

/**
 * localStorage key holding the menu-item ids whose `New` tag the user has
 * already cleared by visiting the screen.
 *
 * Plain localStorage, like the changelog's own `SeenStore`, so the store's
 * persist whitelist and rehydration stay untouched — this is a per-device UI
 * flag, and CTX-511 keeps it that way on purpose.
 *
 * A **set** of ids rather than one value, because the question has one answer
 * per screen: visiting Topology says nothing about Licensing. CTX-591 moved the
 * modal's own state to a set for the same reason — but they stay two keys, not
 * one. §2.7 of `CHANGELOG_WHATS_NEW_RULES.md` decides that explicitly: this
 * answers "has the user been to this screen", `seenEntries` answers "did the
 * user read the announcement", and collapsing them would let reading a modal
 * clear marks for screens nobody visited.
 */
const CLEARED_KEY = "ctx:changelog:clearedNewItems";

/** Cross-tab + same-tab notification that the cleared set changed. */
const CLEARED_EVENT = "ctx:changelog:new-cleared";

/**
 * How long a `New` survives without being visited.
 *
 * The second of the two endings, and the one nobody triggers: an item nobody
 * opens would otherwise wear the tag forever, and a mark that never clears
 * stops carrying information — the rail ends up decorated rather than
 * annotated. 30 days is one release cycle's worth of grace.
 */
export const NEW_TAG_MAX_AGE_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * A menu row named by the latest release, and the route that clears it.
 *
 * The route comes from the entry's `walkthrough`; the row is **derived** from
 * it (CTX-519). An entry used to carry a `menuItemId` beside the path, which
 * made the author repeat what the menu already knows and left two things to
 * keep in step whenever a route moved.
 */
interface NewItemCandidate {
  menuItemId: string;
  path: string;
}

/**
 * A candidate while it is still competing for one of the
 * `MAX_SIMULTANEOUS_NEW_TAGS` slots.
 *
 * The two extra fields exist only to order that competition and are dropped
 * before the result leaves `selectNewItemCandidates` — nothing downstream has a
 * use for a date, and carrying one would invite a second age check beside
 * `isWithinNewWindow`.
 */
interface DatedNewItemCandidate extends NewItemCandidate {
  releasedAt: string;
  entryKey: string;
}

/**
 * Every menu tree a route could belong to.
 *
 * Assembled here rather than taken from the rendered rail because the
 * derivation must not depend on what this tenant happens to see: an id for a
 * row the tenant lacks simply never matches a rendered row, which is the
 * behaviour `MenuItem.newTag.test.tsx` pins. Filtering here instead would make
 * the tag depend on menu state that arrives asynchronously.
 */
const ALL_MENU_TREES: MenuItem[] = [
  ...mainItems,
  ...serviceItems,
  ...eventManagementItems,
  ...managementItems,
  ...accountAreaItems,
  ...reflectorV2MenuItems,
  ...insightMenuItems,
];

/**
 * The menu row a walkthrough's route belongs to, or `null`.
 *
 * `findMenuItemByPath` is the rail's own answer to "which row does this URL
 * light up" — exact match, segment-boundary prefix, query strings and the route
 * alias map, all already handled. Reusing it is the point: a second matcher
 * here is exactly the drift this change removes.
 *
 * It returns the *first* match in tree order rather than the most specific, so
 * an ancestor route listed before a descendant would win. No menu item carries
 * such an ancestor path today (`/` does not prefix-match, and `/kontrol` is
 * guarded as exact-only), and `resolves a nested route to its own row` in the
 * tests is what would catch it if one were added.
 */
function menuItemIdForPath(path: string): string | null {
  const item = findMenuItemByPath(ALL_MENU_TREES, path);
  return item?.id ?? null;
}

function readCleared(): Set<string> {
  try {
    const raw = window.localStorage.getItem(CLEARED_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    // Anything but an array of strings is treated as absent rather than
    // repaired: a tag that fails open (shows) is noise on every row, and a
    // corrupt value is not evidence that the user has not visited anything.
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    // Unreadable storage degrades to "everything already cleared" — no tag —
    // which is the quiet failure. The loud one would be tagging the whole rail.
    return new Set();
  }
}

function writeCleared(ids: Set<string>): void {
  try {
    window.localStorage.setItem(CLEARED_KEY, JSON.stringify([...ids]));
  } catch {
    // Ignore — a full or blocked storage just means the tag returns next load.
  }
}

/**
 * True when `pathname` is the walkthrough's screen or something inside it.
 *
 * Prefix-matched on a segment boundary, not `startsWith` alone: a user reading
 * `/insight/topology/detail/vpc-1` has plainly visited Topology, while
 * `/insight/topology-export` is a different screen that must not clear its tag.
 */
export function isPathVisited(pathname: string, walkthroughPath: string): boolean {
  if (pathname === walkthroughPath) return true;
  return pathname.startsWith(`${walkthroughPath}/`);
}

/**
 * True while a change is still recent enough to be worth pointing at.
 *
 * Measured per entry, from that entry's own `releasedAt`, not from the release
 * it arrived in. A release is a *month* (see below), so a group-wide age would
 * give an entry published on the 28th two days of tag while one from the 1st
 * got thirty — the tag would be timing the batch instead of the change.
 */
function isWithinNewWindow(releasedAt: string, now: number): boolean {
  const ageMs = now - Date.parse(`${releasedAt}T00:00:00Z`);
  // `Number.isNaN` is explicit because NaN fails every comparison — a malformed
  // date would otherwise read as fresh and tag the rail indefinitely.
  if (Number.isNaN(ageMs)) return false;
  return ageMs <= NEW_TAG_MAX_AGE_DAYS * MS_PER_DAY;
}

/**
 * How many rows may wear a `New` at once.
 *
 * The third ending, and the only one that does not depend on time. The window
 * and the visit both bound how *long* a single tag lives; neither bounds how
 * many appear together, and a month with six new screens would mark six rows of
 * a twenty-row rail at once. At that density the tag stops being an exception
 * worth following and becomes decoration — the same failure that a `New` which
 * never expires produces, arrived at from the other side (CTX-502).
 *
 * Three is the count that still reads as "these ones": the overflow is not lost,
 * it is in the What's new modal and in `/changelog`, which is where a reader who
 * wants the full list is going anyway.
 */
export const MAX_SIMULTANEOUS_NEW_TAGS = 3;

/**
 * The rows a release names, before the visited set is subtracted.
 *
 * Exported for its own tests — the endings and this selection are the whole of
 * the feature, and they are worth exercising without a React tree or a fetch
 * around them.
 *
 * **The caller passes a release, this does not decide what one is.** That rule
 * belongs to `getLatestReleaseGroup()`, which the What's new modal already
 * walks: a release is the newest *month*, because the endpoint does not
 * serialize deployment identity and at ~5 entries/month a per-day batch is
 * almost always a single entry (CTX-224). Deriving a second answer here is how
 * the rail ends up tagging less than the modal announced.
 *
 * At most `MAX_SIMULTANEOUS_NEW_TAGS` rows come back, newest first.
 */
export function selectNewItemCandidates(
  entries: ChangelogEntry[],
  now: number
): NewItemCandidate[] {
  const candidates = new Map<string, DatedNewItemCandidate>();
  for (const entry of entries) {
    if (!isWithinNewWindow(entry.releasedAt, now)) continue;
    const path = entry.walkthrough?.path;
    if (!path) continue;
    // A route the menu does not own — a detail screen with no row, or one this
    // build does not ship — is not a gap to fill. It simply tags nothing.
    const menuItemId = menuItemIdForPath(path);
    if (!menuItemId) continue;
    // First entry wins a repeated row: two changes to one screen are still one
    // `New`, and the newest is the one the reader met at the top of the modal.
    if (!candidates.has(menuItemId))
      candidates.set(menuItemId, {
        menuItemId,
        path,
        releasedAt: entry.releasedAt,
        entryKey: entry.entryKey,
      });
  }
  // Sorted here rather than trusting the feed's order: the cap makes the order
  // load-bearing — it decides which rows are dropped — and a selection that
  // depends on how the API happened to sort is one that changes silently.
  // `entryKey` breaks the tie because a release is a month, so same-day entries
  // are the common case, not the edge one.
  return [...candidates.values()]
    .sort(
      (a, b) =>
        b.releasedAt.localeCompare(a.releasedAt) ||
        a.entryKey.localeCompare(b.entryKey)
    )
    .slice(0, MAX_SIMULTANEOUS_NEW_TAGS)
    .map(({ menuItemId, path }) => ({ menuItemId, path }));
}

/**
 * The set of menu-item ids that should currently wear a `New` tag, plus the
 * route-watching that clears them.
 *
 * Called **once**, at the SideMenu root, and shared through
 * `ChangelogNewItemsContext` — every row consuming this directly would mean one
 * fetch state, one effect and one storage listener per row. The underlying
 * `getChangelog()` memoises one request per session, shared with the page and
 * the footer card, so the cost this avoids is React work, not network.
 *
 * @param pathname Current route, passed in rather than read from `useLocation`
 * so the clearing rule can be exercised without a Router around it.
 * @param tenantType Passed in for the same reason — a test of the clearing rule
 * should not need a Redux store to say who is looking.
 */
export function useChangelogNewItems(
  pathname: string,
  tenantType: string
): Set<string> {
  const axiosClient = useAxiosAuth0();
  const enabled = canSeeChangelog(tenantType);
  const [candidates, setCandidates] = useState<NewItemCandidate[]>([]);
  const [cleared, setCleared] = useState<Set<string>>(readCleared);

  /**
   * Where the changelog is hidden the feed is not fetched at all — the gate is
   * about the feature existing here, so a request whose result can never render
   * is not a cheap no-op, it is traffic for nothing.
   *
   * A failure is swallowed for the same reason `useUnseenRelease` swallows its
   * own: this is an ambient mark in the rail, and the honest degraded state is
   * no tag rather than an error raised somewhere the user never looked.
   */
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    getLatestReleaseGroup(axiosClient)
      .then((group) => {
        if (alive) setCandidates(selectNewItemCandidates(group?.entries ?? [], Date.now()));
      })
      .catch(() => {
        /* no tags */
      });
    return () => {
      alive = false;
    };
  }, [axiosClient, enabled]);

  // Keep in sync when another tab (or this one) clears an item.
  useEffect(() => {
    const sync = () => setCleared(readCleared());
    window.addEventListener("storage", sync);
    window.addEventListener(CLEARED_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(CLEARED_EVENT, sync);
    };
  }, []);

  const clearItem = useCallback((menuItemId: string) => {
    setCleared((prev) => {
      if (prev.has(menuItemId)) return prev;
      const next = new Set(prev).add(menuItemId);
      writeCleared(next);
      // Same-tab listeners get nothing from `storage`, which only fires in the
      // *other* tabs — so the event is dispatched explicitly, as `markSeen` does.
      window.dispatchEvent(new Event(CLEARED_EVENT));
      return next;
    });
  }, []);

  /**
   * Visiting the screen is what clears the row — not clicking it in the rail.
   * `activeItem` in `SideMenu` is set by click handlers only, so keying off it
   * would leave a tag standing after a direct URL, a bookmark or a reload,
   * which are exactly how someone returns to a screen they were shown once.
   */
  useEffect(() => {
    for (const candidate of candidates) {
      if (isPathVisited(pathname, candidate.path)) clearItem(candidate.menuItemId);
    }
  }, [candidates, pathname, clearItem]);

  return useMemo(
    () =>
      new Set(
        candidates
          .filter((candidate) => !cleared.has(candidate.menuItemId))
          .map((candidate) => candidate.menuItemId)
      ),
    [candidates, cleared]
  );
}
